import React, { useEffect, useState } from "react";
import {
  Alert,
  Checkbox,
  Col,
  Collapse,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import { saveProfile } from "./api";
import { GRADE_LABELS, SOURCE_LABELS, TRADE_LABELS } from "./constants";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";

const SOURCE_HINTS = {
  doe: "All German public tenders published on oeffentlichevergabe.de (open data).",
  ted: "EU-wide tenders above the thresholds. Mostly also on DÖE; duplicates are merged.",
  blauarbeit:
    "Private requests from blauarbeit.de. Only requests in your trade and area are kept. You answer the customer on the portal."
};

const SearchProfileModal = ({ open, profile, meta, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && profile) form.setFieldsValue(profile);
  }, [open, profile]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await saveProfile(values);
      Success(t("Search profile saved."));
      onSaved(res.data);
    } catch (err) {
      if (!err?.errorFields) generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  const regionOptions = (meta?.regions || []).map(r => ({ value: r.code, label: r.nameDe }));
  const euro = { addonAfter: "€", style: { width: "100%" }, min: 0 };
  const percent = { addonAfter: "%", style: { width: "100%" }, min: 0 };

  return (
    <Modal
      centered
      width={760}
      open={open}
      title={t("Search profile")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      onOk={handleSave}
      onCancel={onClose}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="trades"
          label={t("Your trades")}
          rules={[
            { required: true, type: "array", min: 1, message: t("Choose at least one trade.") }
          ]}
        >
          <Checkbox.Group style={{ width: "100%" }}>
            <Row gutter={[16, 8]}>
              {(meta?.trades || []).map(tr => (
                <Col xs={24} sm={12} key={tr.code}>
                  <Checkbox value={tr.code}>{t(TRADE_LABELS[tr.code] || tr.nameDe)}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="postalCode"
              label={t("Postcode of your company")}
              rules={[
                {
                  required: true,
                  pattern: /^\d{5}$/,
                  message: t("Please enter a valid German postcode.")
                }
              ]}
              extra={profile?.baseLabel ? `${t("Location used")}: ${profile.baseLabel}` : null}
            >
              <Input maxLength={5} placeholder="50667" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="serviceRadiusKm" label={t("Service radius")}>
              <InputNumber min={5} max={1000} addonAfter="km" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="allowedRegions" label={t("Only these federal states (optional)")}>
              <Select mode="multiple" allowClear options={regionOptions} placeholder={t("All")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="excludedRegions" label={t("Exclude federal states")}>
              <Select mode="multiple" allowClear options={regionOptions} />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Text strong>{t("Sources")}</Typography.Text>
        {["doe", "ted", "blauarbeit"].map(id => (
          // Plain flex, not Row/Col: the phone rescue layer stacks form columns without an xs size.
          <Flex key={id} align="center" gap={12} style={{ marginBlock: 8 }}>
            <Form.Item name={["sources", id]} valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div>{t(SOURCE_LABELS[id])}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t(SOURCE_HINTS[id])}
              </Typography.Text>
            </div>
          </Flex>
        ))}

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col xs={24} md={8}>
            <Form.Item name="minOrderValueEur" label={t("Minimum order value")}>
              <InputNumber {...euro} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="maxOrderValueEur" label={t("Maximum order value")}>
              <InputNumber {...euro} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="minLeadDays" label={t("Days needed to prepare an offer")}>
              <InputNumber min={0} max={90} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          size="small"
          items={[
            {
              key: "pricing",
              label: t("Prices for the offer calculation"),
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={t(
                      "Enter the hourly rates you charge customers (Stundenverrechnungssatz). Overhead is usually already included in them."
                    )}
                  />
                  <Row gutter={16}>
                    {Object.keys(GRADE_LABELS).map(g => (
                      <Col xs={12} md={6} key={g}>
                        <Form.Item
                          name={["pricing", "billableRates", g]}
                          label={t(GRADE_LABELS[g])}
                        >
                          <InputNumber {...euro} addonAfter="€/h" max={500} />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                  <Row gutter={16}>
                    <Col xs={12} md={6}>
                      <Form.Item
                        name={["pricing", "materialMarkupPct"]}
                        label={t("Material markup")}
                      >
                        <InputNumber {...percent} max={200} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name={["pricing", "overheadPct"]} label={t("Overhead")}>
                        <InputNumber {...percent} max={200} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name={["pricing", "riskPct"]} label={t("Risk")}>
                        <InputNumber {...percent} max={50} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name={["pricing", "targetMarginPct"]} label={t("Target margin")}>
                        <InputNumber {...percent} max={60} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={12} md={6}>
                      <Form.Item name={["pricing", "vatPct"]} label={t("VAT")}>
                        <InputNumber {...percent} max={30} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        name={["pricing", "travelRatePerKm"]}
                        label={t("Travel cost per km")}
                      >
                        <InputNumber {...euro} step={0.05} max={10} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        name={["pricing", "travelHourlyRate"]}
                        label={t("Travel time per hour")}
                      >
                        <InputNumber {...euro} max={500} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item
                        name={["pricing", "minimumOrderValue"]}
                        label={t("Minimum order value")}
                      >
                        <InputNumber {...euro} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  );
};

export default SearchProfileModal;
